/* ═════════════════════════ the week ═════════════════════════

   Weekly planning: the week itself, laid out against the hours that really
   exist. What the week is made of is declared here — the lectures and meetings
   that repeat, the psets and readings that come due, the gym — and the engine
   turns that into blocks in the gaps.

   Two of the app's rulings live on this grid. Moving a block says the solver
   was wrong about where; missing one says the slot was a fiction, and that is
   the only way the next plan can learn that Friday at eight in the morning
   never happens. */
import { wkKind, wkMode } from '../core/domain.js';
import { predictMin } from '../state/evidence.js';
import { COURSE_COLORS } from '../core/palette.js';
import { DAY_MS, DOW, clamp, dowIndex, fmtDay, fmtDurMin, fromIsoDay, hhmmToMin, isoDay, minToHhmm, uid } from '../core/time.js';
import { SYNC } from '../platform/bridge.js';
import { LEDGER, WEEK, recomputePlans, touchPlan, weekPlanIsCurrent, wkStart } from '../state/plans.js';
import { S, courseColor, save } from '../state/store.js';
import { askText, closeModal, el, openModal, toast } from './dom.js';
import { fmtMinOfDay } from './format.js';
import { initSourceIntake, renderSourceList } from './longterm.js';
import { renderCourseSelects } from './tasks.js';
import { $ } from './dom.js';
import { courseName } from '../state/store.js';
import StudyHubEngine from '../core/engine.js';

export const wkDayMs = (i) => wkStart() + i * DAY_MS;

export const wkItems = () => {
  const f = S.weekly.filterCourse;
  return f ? S.weekly.items.filter((it) => it.courseId === f) : S.weekly.items;
};

/** Which of this week's seven days an item falls on, as Monday-first indices. */
export function itemDays(it) {
  if (it.recur === 'once') {
    const ms = fromIsoDay(it.date);
    if (ms == null) return [];
    const i = Math.floor((ms - wkStart()) / DAY_MS);
    return i >= 0 && i < 7 ? [i] : [];
  }
  // stored as JS day numbers (0 = Sunday) because that is what Date gives back
  return it.days.map((d) => (d + 6) % 7).sort((a, b) => a - b);
}

/** Blocks as drawn: the plan's, minus what you missed, plus where you moved it.
 *
 *  `unfiltered` exists for readers outside this tab. The course filter is a
 *  view control belonging to the weekly grid, and a filter left on there must
 *  not quietly hide half of today from the landing page — which would also put
 *  the clock's block list and `WEEK.days[]`'s totals into disagreement, and the
 *  whole point of that fold is that the two are the same numbers.
 *  @param {{ unfiltered?: boolean }} [opts] */
export function liveBlocks(opts) {
  if (!weekPlanIsCurrent()) return [];
  const f = opts && opts.unfiltered ? '' : S.weekly.filterCourse;

  return WEEK.blocks.map((b) => {
    const moved = S.weekly.moved[b.id] || null;
    const day = moved ? moved.day : b.day;
    const startMin = hhmmToMin(moved ? moved.start : b.start);
    const endMin = hhmmToMin(moved ? moved.end : b.end);
    const item = b.itemId ? S.weekly.items.find((x) => x.id === b.itemId) : null;
    const dayIdx = Math.floor((fromIsoDay(day) - wkStart()) / DAY_MS);

    return {
      ...b, item, moved: !!moved, dayIdx, startMin, endMin,
      missed: S.weekly.misses.some((m) => m.blockId === b.id) && !moved,
      courseId: item ? item.courseId : (b.courseId || ''),
    };
  })
  .filter((b) => b.dayIdx >= 0 && b.dayIdx < 7)
  .filter((b) => !f || b.courseId === f);
}

/* ─────────────────── free time, computed exactly ───────────────────

   Rescheduling and planning are both done here now, and both are subtraction:
   where the gaps are is not a judgement call, and the app holds every input it
   needs. The reason it matters that this is local is latency — pressing
   "couldn't make it" has to answer instantly, or you will not press it, and a
   plan you have to wait for is a plan you stop asking for. */

/** Everything already spoken for this week, as [dayIdx][{from,to}] minutes. */
export function busyByDay(exceptBlockId) {
  const days = [[], [], [], [], [], [], []];

  // pinned weekly items — the lectures and shifts
  S.weekly.items.forEach((it) => {
    if (wkMode(it.kind) !== 'fixed') return;
    const at = hhmmToMin(it.time);
    if (at == null) return;
    itemDays(it).forEach((d) => days[d].push({ from: at, to: at + (it.durMin || 60), what: it.title }));
  });

  // the plan's own blocks, so two suggestions never land on each other
  liveBlocks().forEach((b) => {
    if (b.id === exceptBlockId || b.missed) return;
    days[b.dayIdx].push({ from: b.startMin, to: b.endMin, what: b.title });
  });

  /* And studyHub's real calendar: events, anything scheduled onto a clock, and
     the Google side of it. A lecture that only ever existed in Google is still
     an hour you are not free, and while this only misplaced a suggestion it was
     a small wrong; under a planner that reasons about capacity it becomes a
     start-by date that is wrong by a day. All-day rows are left out on purpose —
     an all-day Google entry is a label on the day, not six hours of it. */
  const from = wkStart(), to = from + 7 * DAY_MS;
  const google = (SYNC.events || [])
    .filter((e) => !e.allDay)
    .map((e) => {
      const start = new Date(e.start).getTime();
      return { title: e.title || '(no title)', start, end: e.end ? new Date(e.end).getTime() : start + 3600000 };
    });
  S.events.concat(S.tasks.filter((t) => !t.done && typeof t.start === 'number'), google)
    .forEach((it) => {
      if (typeof it.start !== 'number' || it.start < from || it.start >= to) return;
      const d = Math.floor((it.start - from) / DAY_MS);
      const s = new Date(it.start);
      const startMin = s.getHours() * 60 + s.getMinutes();
      const endMs = it.end || (it.start + (it.estMin || 60) * 60000);
      days[d].push({ from: startMin, to: startMin + Math.round((endMs - it.start) / 60000), what: it.title });
    });

  return days.map((list) => list.sort((a, b) => a.from - b.from));
}

/** The holes left in a day once everything above is subtracted out. */
export function freeGaps(dayIdx, busy) {
  const gaps = [];
  let cursor = S.weekly.dayStart;
  (busy[dayIdx] || []).forEach(({ from, to }) => {
    if (from > cursor) gaps.push({ from: cursor, to: Math.min(from, S.weekly.dayEnd) });
    cursor = Math.max(cursor, to);
  });
  if (cursor < S.weekly.dayEnd) gaps.push({ from: cursor, to: S.weekly.dayEnd });
  return gaps.filter((g) => g.to > g.from);
}

/** Where a missed block could go instead, best first, at most three.

    "Best" is deliberately conservative: the same day if the day is not over,
    then the days after it, at roughly the hour it was meant to happen — a slot
    you already proved you can keep is worth more than a theoretically emptier
    one at seven in the morning. Nothing is ever suggested past a work item's
    due moment, because that is not a reschedule, it is a miss with extra steps. */
export function alternativesFor(block) {
  const dur = Math.max(15, block.endMin - block.startMin);
  const busy = busyByDay(block.id);
  const now = new Date();
  const todayIdx = dowIndex(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const dueIdx = block.item && wkMode(block.item.kind) === 'work' ? itemDays(block.item)[0] : null;
  const dueMin = block.item ? hhmmToMin(block.item.time) : null;

  const out = [];
  for (let d = block.dayIdx; d < 7 && out.length < 12; d++) {
    freeGaps(d, busy).forEach((g) => {
      let from = g.from;
      if (d === todayIdx) from = Math.max(from, Math.ceil((nowMin + 15) / 15) * 15);   // no travelling backwards
      if (d < todayIdx) return;
      if (g.to - from < dur) return;

      // past the deadline is not an alternative
      if (dueIdx != null && (d > dueIdx || (d === dueIdx && dueMin != null && from + dur > dueMin))) return;

      // aim for the hour it was supposed to happen, but stay inside the gap
      const wanted = clamp(block.startMin, from, g.to - dur);
      out.push({
        dayIdx: d,
        from: wanted,
        to: wanted + dur,
        sameDay: d === block.dayIdx,
        drift: Math.abs(wanted - block.startMin),
      });
    });
  }

  return out
    .sort((a, b) => (a.dayIdx - b.dayIdx) || (a.drift - b.drift))
    .slice(0, 3);
}

export function renderWeekGrid() {
  const host = $('wpGrid');
  if (!host) return;
  host.textContent = '';

  const nothing = !S.weekly.items.length;
  $('wpEmpty').hidden = !nothing;
  host.hidden = nothing;
  if (nothing) return;

  const blocks = liveBlocks();
  const items = wkItems();
  const todayIdx = dowIndex(new Date());

  for (let d = 0; d < 7; d++) {
    const col = el('div', 'wp-day');
    if (d === todayIdx) col.classList.add('is-today');
    col.dataset.day = isoDay(wkDayMs(d));

    const head = el('div', 'wp-day-head');
    head.appendChild(el('strong', null, DOW[(d + 1) % 7]));
    head.appendChild(el('span', 'wp-day-date', fmtDay(wkDayMs(d))));
    col.appendChild(head);

    const body = el('div', 'wp-day-body');

    /* Two things share a column: what the plan placed, and — for anything it
       could not place — the raw items that fall on this day. A placed block
       always wins, so nothing is ever shown twice. */
    const planned = blocks.filter((b) => b.dayIdx === d).sort((a, b) => a.startMin - b.startMin);
    const placedIds = new Set(planned.map((b) => b.itemId).filter(Boolean));
    const unplanned = items
      .filter((it) => !placedIds.has(it.id) && itemDays(it).includes(d))
      .sort((a, b) => (hhmmToMin(a.time) ?? 1441) - (hhmmToMin(b.time) ?? 1441));

    planned.forEach((b) => body.appendChild(wpBlockNode(b)));
    unplanned.forEach((it) => body.appendChild(wpItemNode(it, d)));

    if (!planned.length && !unplanned.length) body.appendChild(el('div', 'wp-day-clear', 'clear'));

    col.appendChild(body);
    host.appendChild(col);
  }
}

/** One block the plan placed, with the controls that let you argue with it. */
export function wpBlockNode(b) {
  const mode = b.item ? wkMode(b.item.kind) : (b.kind || 'work');
  const node = el('div', `wp-block is-${mode}`);
  node.style.setProperty('--chip', courseColor(b.courseId));
  node.dataset.block = b.id;
  /* The reason a block sits where it does is worth exactly what the evidence
     behind its cost is worth, so the rung rides on the block and the why-line
     is coloured by it. A column of faint why-lines is a week built on guesses. */
  if (b.rung) node.dataset.rung = b.rung;
  if (b.missed) node.classList.add('is-missed');
  if (b.moved) node.classList.add('is-moved');

  node.appendChild(el('div', 'wp-b-time', `${fmtMinOfDay(b.startMin)} – ${fmtMinOfDay(b.endMin)}`));
  node.appendChild(el('div', 'wp-b-title', b.title));
  if (b.why) node.appendChild(el('div', 'wp-b-why', b.why));

  /* The working, behind a toggle. Every block's argument unfolded at once is a
     report rather than a plan; a plan whose working cannot be reached at all
     is one you can only obey.

     The exception is a block belonging to something that did not fit. That is
     the case where the plan is asking to be doubted, so it makes the argument
     without being asked. */
  const trace = b.traceKey && LEDGER ? StudyHubEngine.explain(LEDGER, b.traceKey) : null;
  if (trace) {
    const short = !(trace.verdict && trace.verdict.unplacedMin > 0);
    const toggle = el('button', 'btn btn-ghost wp-alt', short ? 'why?' : 'hide the working');
    toggle.title = 'How this block was arrived at';
    node.appendChild(toggle);

    /* The block, not the trace: wpTraceNode needs the block id as well as the
       key, because the runners-up it prints are the ones rejected for this
       block's own slot and `placed` holds a row per block. */
    let shown = short ? null : node.appendChild(wpTraceNode(b));
    toggle.addEventListener('click', () => {
      if (shown) { shown.remove(); shown = null; toggle.textContent = 'why?'; return; }
      shown = wpTraceNode(b);
      toggle.textContent = 'hide the working';
      toggle.after(shown);
    });
  }

  if (b.moved) node.appendChild(el('div', 'wp-b-flag', 'moved by you'));

  const acts = el('div', 'wp-b-acts');
  if (b.missed) {
    // the alternatives are computed the moment they are asked for, so they
    // reflect every other move already made this week
    const alts = alternativesFor(b);
    node.appendChild(el('div', 'wp-b-flag', alts.length ? 'missed — try instead:' : 'missed — nothing else fits this week'));
    const list = el('div', 'wp-alts');
    alts.forEach((a) => {
      const pick = el('button', 'btn wp-alt', `${DOW[(a.dayIdx + 1) % 7]} ${fmtMinOfDay(a.from)}`);
      pick.title = `Move “${b.title}” to ${DOW[(a.dayIdx + 1) % 7]} at ${fmtMinOfDay(a.from)}`;
      pick.addEventListener('click', () => moveBlock(b, a));
      list.appendChild(pick);
    });
    const keep = el('button', 'btn btn-ghost wp-alt', 'Leave it');
    keep.addEventListener('click', () => unmissBlock(b.id));
    list.appendChild(keep);
    node.appendChild(list);
  } else {
    const miss = el('button', 'btn btn-ghost wp-miss', '⊘ Couldn’t make it');
    miss.title = 'Mark this as missed and see where else it fits';
    miss.addEventListener('click', () => missBlock(b));
    acts.appendChild(miss);
    node.appendChild(acts);
  }

  return node;
}

/** The whole argument for one block, unfolded.

    Every line of it is a quantity or a name, and both are there to be checked
    rather than read — which is why it is a stack of short monospaced lines and
    not a paragraph. The gate strip goes first because it is the line that says
    nothing was silently dropped, which is the thing hardest to take on trust. */
export function wpTraceNode(b) {
  const t = StudyHubEngine.explain(LEDGER, b.traceKey);
  if (!t) return null;

  const box = el('div', 'wp-b-trace');
  const line = (text, cls) => box.appendChild(el('div', cls || null, text));

  if (t.gates && t.gates.length) {
    line(t.gates.map((g) => `${g.name} ${g.ok ? '✓' : '—'}`).join('  '));
    const failed = t.gates.find((g) => !g.ok && g.note);
    if (failed) line(failed.note);
  }

  if (t.priced) {
    const p = t.priced;
    const range = p.high > p.min ? `, ${fmtDurMin(p.high)} if it runs long` : '';
    line(`${fmtDurMin(p.min)}${range} — ${p.basis}`);
  }

  (t.merged || []).forEach((m) => line(`↳ absorbs ${m.title}${m.why ? ` — ${m.why}` : ''}`, 'is-merged'));

  if (t.credited && t.credited.loggedMin > 0) {
    line(`${fmtDurMin(Math.round(t.credited.loggedMin))} already logged, ${fmtDurMin(t.credited.owedMin)} owed`);
  }

  if (t.capacity && t.capacity.length) {
    line(t.capacity.map((c) => `${c.dayLabel} ${fmtDurMin(c.freeMin)}`).join(' · '));
  }

  if (t.verdict && t.verdict.sentence) line(t.verdict.sentence);

  const here = (t.placed || []).find((p) => p.blockId === b.id);
  (here && here.rejected || []).forEach((r) => line(`not ${r.day} ${r.from} — ${r.reason}`));

  return box;
}

/** An item with no block: work the week owes that had nowhere to go. */
export function wpItemNode(it, dayIdx) {
  const k = wkKind(it.kind);
  const node = el('button', `wp-item is-${k.mode}`);
  node.style.setProperty('--chip', courseColor(it.courseId));

  const at = hhmmToMin(it.time);
  node.appendChild(el('span', 'wp-i-glyph', k.glyph));
  node.appendChild(el('span', 'wp-i-title', it.title));

  const bits = [];
  if (at != null) bits.push(k.mode === 'work' ? `due ${fmtMinOfDay(at)}` : fmtMinOfDay(at));
  if (k.mode === 'work') {
    const p = predictMin(it);
    if (p.min) bits.push(`≈${fmtDurMin(p.min)}`);
  } else if (it.durMin) bits.push(fmtDurMin(it.durMin));
  node.appendChild(el('span', 'wp-i-meta', bits.join(' · ')));

  node.title = `${k.label} · ${courseName(it.courseId)} — click to edit`;
  node.addEventListener('click', () => openWkItem(it.id));
  return node;
}

export function missBlock(b) {
  S.weekly.misses.push({
    blockId: b.id,
    itemId: b.itemId || null,
    title: b.title,
    kind: b.item ? b.item.kind : null,
    day: isoDay(wkDayMs(b.dayIdx)),
    /* The slot as well as the hour it began. A missed block is put back on the
       grid exactly where it was, so the record has to hold enough to rebuild
       it — the hour alone would leave the planner guessing at its length. */
    time: minToHhmm(b.startMin),
    start: minToHhmm(b.startMin),
    end: minToHhmm(b.endMin),
    at: Date.now(),
  });
  delete S.weekly.moved[b.id];
  /* A miss is an observation, and the only one the app has about which hours
     are fiction. It changes both what is still owed this week and where the
     next plan is willing to put anything. */
  touchPlan();
  save();
  renderWeekly();
}

export function unmissBlock(id) {
  S.weekly.misses = S.weekly.misses.filter((m) => m.blockId !== id);
  touchPlan();
  save();
  renderWeekly();
}

export function moveBlock(b, alt) {
  S.weekly.moved[b.id] = {
    day: isoDay(wkDayMs(alt.dayIdx)),
    start: minToHhmm(alt.from),
    end: minToHhmm(alt.to),
  };
  // your own placement is senior to the solver's, and is booked before it runs
  touchPlan();
  save();
  renderWeekly();
  toast(`Moved to ${DOW[(alt.dayIdx + 1) % 7]} at ${fmtMinOfDay(alt.from)}.`);
}

export function renderWkItems() {
  const host = $('wpItems');
  if (!host) return;
  host.textContent = '';

  const items = wkItems();
  const badge = $('wpItemCount');
  badge.textContent = String(items.length);
  badge.classList.toggle('is-zero', !items.length);

  if (!items.length) {
    host.appendChild(el('div', 'empty-list',
      'Nothing in the week yet. Add the lectures and meetings that repeat, the psets and posts that come due, and the gym.'));
    return;
  }

  const order = { fixed: 0, work: 1, habit: 2 };
  items.slice()
    .sort((a, b) => (order[wkMode(a.kind)] - order[wkMode(b.kind)]) || a.title.localeCompare(b.title))
    .forEach((it) => {
      const k = wkKind(it.kind);
      const row = el('div', 'wp-row');
      row.style.setProperty('--chip', courseColor(it.courseId));

      const main = el('div', 'wp-row-main');
      main.appendChild(el('div', 'wp-row-title', `${k.glyph}  ${it.title}`));

      const when = it.recur === 'once'
        ? fmtDay(fromIsoDay(it.date))
        : itemDays(it).map((d) => DOW[(d + 1) % 7]).join(' ');
      const at = hhmmToMin(it.time);
      const bits = [when];
      if (at != null) bits.push(k.mode === 'work' ? `due ${fmtMinOfDay(at)}` : fmtMinOfDay(at));
      if (k.mode === 'habit') bits.push(`${it.perWeek}× a week`);
      if (k.mode === 'work') {
        const p = predictMin(it);
        bits.push(p.n ? `${fmtDurMin(p.min)} (${p.basis})` : `≈${fmtDurMin(p.min)}`);
      } else if (it.durMin) bits.push(fmtDurMin(it.durMin));
      main.appendChild(el('div', 'wp-row-meta', bits.join(' · ')));
      row.appendChild(main);

      const edit = el('button', 'icon-btn', '✎');
      edit.title = 'Edit';
      edit.addEventListener('click', () => openWkItem(it.id));
      row.appendChild(edit);

      host.appendChild(row);
    });
}

export let wkEditId = null;
export let wkKindNow = 'pset';

export function setWkKind(kind) {
  wkKindNow = kind;
  const k = wkKind(kind);
  [...$('wpKindSeg').children].forEach((b) => b.classList.toggle('is-active', b.dataset.kind === kind));
  $('wpKindExplainer').textContent = k.explain;

  $('wpTimeLabel').textContent = k.mode === 'work' ? 'Due at' : 'Starts';
  $('wpTimeField').hidden = k.mode === 'habit';
  $('wpDurField').hidden = k.mode === 'work';
  $('wpEffortBox').hidden = k.mode !== 'work';
  $('wpPerWeekField').hidden = k.mode !== 'habit';
  $('wpDaysField').hidden = k.mode === 'habit';

  updateWkMeta();
}

export function wkEffortMin() { return (+$('wpEffortH').value || 0) * 60 + (+$('wpEffortM').value || 0); }

export function updateWkMeta() {
  const k = wkKind(wkKindNow);
  if (k.mode !== 'work') {
    $('wpMeta').textContent = k.mode === 'habit'
      ? 'These go in whatever the rest of the week leaves, spread out rather than stacked.'
      : 'Pinned to its hour. Everything else is planned around it.';
    return;
  }

  const probe = { kind: wkKindNow, title: $('wpTitle').value, courseId: $('wpCourse').value, estMin: wkEffortMin() };
  const p = predictMin(probe);
  /* The basis says on what grounds all by itself, so the sentence around it
     only has to stop short of claiming more. The tail is the one thing the
     basis cannot say, and it is only true when something was measured. */
  $('wpMeta').textContent = `studyHub plans ${fmtDurMin(p.min)} for this — ${p.basis}.`
    + (p.n ? ' That is the number it uses, not your estimate.' : '');
}

export function openWkItem(id) {
  const it = id ? S.weekly.items.find((x) => x.id === id) : null;
  wkEditId = it ? it.id : null;

  $('wpModalTitle').textContent = it ? 'Edit this' : 'Add to the week';
  $('wpSave').textContent = it ? 'Save changes' : 'Add it';
  $('wpDelete').hidden = !it;

  const sel = $('wpCourse');
  sel.textContent = '';
  S.courses.forEach((c) => sel.appendChild(new Option(c.name, c.id)));
  sel.value = (it && it.courseId) || S.weekly.filterCourse || (S.courses[0] || {}).id || '';

  $('wpTitle').value = it ? it.title : '';
  $('wpNotes').value = it ? (it.notes || '') : '';
  $('wpTime').value = it && it.time ? it.time : '';
  $('wpDur').value = it && it.durMin ? it.durMin : 60;
  $('wpPerWeek').value = it ? it.perWeek : 3;
  $('wpRecur').value = it ? it.recur : 'weekly';
  $('wpDate').value = it && it.date ? it.date : isoDay(Date.now());

  const eff = it ? it.estMin || 0 : 60;
  $('wpEffortH').value = Math.floor(eff / 60);
  $('wpEffortM').value = eff % 60;

  const days = it ? it.days : [];
  [...$('wpDays').children].forEach((b) => b.classList.toggle('is-on', days.includes(+b.dataset.dow)));

  setWkRecur(it ? it.recur : 'weekly');
  setWkKind(it ? it.kind : 'pset');
  openModal('wpModal');
  setTimeout(() => $('wpTitle').focus(), 30);
}

export function setWkRecur(mode) {
  $('wpRecur').value = mode;
  $('wpDaysField').hidden = mode === 'once' || wkMode(wkKindNow) === 'habit';
  $('wpDateField').hidden = mode !== 'once';
}

export function saveWkItem() {
  const title = $('wpTitle').value.trim();
  if (!title) { toast('Give it a name.', true); return; }

  const mode = wkMode(wkKindNow);
  const recur = $('wpRecur').value === 'once' ? 'once' : 'weekly';
  const days = [...$('wpDays').children].filter((b) => b.classList.contains('is-on')).map((b) => +b.dataset.dow);

  /* A habit does not sit on named days — that is the whole point of it — so it
     is the one kind allowed to have none. */
  if (mode !== 'habit' && recur === 'weekly' && !days.length) {
    toast('Pick at least one day.', true);
    return;
  }
  if (recur === 'once' && !fromIsoDay($('wpDate').value)) { toast('Pick a date.', true); return; }

  const patch = {
    kind: wkKindNow,
    title,
    courseId: $('wpCourse').value,
    recur,
    days: mode === 'habit' ? [] : (recur === 'weekly' ? days : []),
    date: recur === 'once' ? $('wpDate').value : null,
    time: mode === 'habit' ? null : ($('wpTime').value || null),
    durMin: mode === 'work' ? 0 : Math.max(0, +$('wpDur').value || 60),
    estMin: mode === 'work' ? wkEffortMin() : 0,
    perWeek: mode === 'habit' ? clamp(+$('wpPerWeek').value || 3, 1, 14) : 1,
    notes: $('wpNotes').value.trim(),
  };

  const existing = wkEditId && S.weekly.items.find((x) => x.id === wkEditId);
  if (existing) { Object.assign(existing, patch); toast('Updated.'); }
  else { S.weekly.items.push({ id: uid(), created: Date.now(), ...patch }); toast('In the week.'); }

  touchPlan();
  save();
  closeModal('wpModal');
  renderWeekly();
}

export function deleteWkItem() {
  if (!wkEditId) return;
  S.weekly.items = S.weekly.items.filter((x) => x.id !== wkEditId);
  touchPlan();
  save();
  closeModal('wpModal');
  renderWeekly();
  toast('Removed from the week.');
}

export function renderWeekNotes() {
  const host = $('wpNotesList');
  if (!host) return;
  host.textContent = '';

  const notes = weekPlanIsCurrent() ? WEEK.notes : [];
  const badge = $('wpNoteCount');
  badge.textContent = String(notes.length);
  badge.classList.toggle('is-zero', !notes.length);

  if (!notes.length) {
    host.appendChild(el('div', 'empty-list', WEEK.status === 'error'
      ? (WEEK.message || 'The layout hit a problem.')
      : 'Nothing to weigh yet. Add what your week is made of and the reasoning appears here.'));
    return;
  }

  notes.forEach((n) => {
    const card = el('div', `sugg-item wp-note is-${n.kind || 'note'}`);
    card.appendChild(el('div', 'wp-note-kind', n.kind || 'note'));
    card.appendChild(el('div', 'sg-title', n.title));
    if (n.body) card.appendChild(el('div', 'wp-note-body', n.body));
    host.appendChild(card);
  });
}

/** What you did not manage, so the tab is honest about the week you had. */
export function renderMisses() {
  const host = $('wpMisses');
  if (!host) return;
  host.textContent = '';

  /* Grouped by the slot itself rather than the block: three missed Friday
     eight-o'clocks is one fact about your life, not three. */
  const bucket = new Map();
  S.weekly.misses.forEach((m) => {
    const key = `${new Date(fromIsoDay(m.day)).getDay()}|${m.time}|${m.title}`;
    if (!bucket.has(key)) bucket.set(key, { ...m, count: 0 });
    bucket.get(key).count++;
  });
  const rows = [...bucket.values()].sort((a, b) => b.count - a.count || b.at - a.at).slice(0, 8);

  if (!rows.length) {
    host.appendChild(el('div', 'empty-list',
      'Nothing missed. When a block does not happen, press “Couldn’t make it” on it and the alternatives appear on the spot.'));
    return;
  }

  rows.forEach((m) => {
    const row = el('div', 'wp-miss-row');
    row.appendChild(el('div', 'wp-miss-title', m.title));
    row.appendChild(el('div', 'wp-miss-meta',
      `${DOW[new Date(fromIsoDay(m.day)).getDay()]} ${m.time}${m.count > 1 ? ` · missed ${m.count}×` : ''}`));
    host.appendChild(row);
  });

  const clear = el('button', 'btn btn-ghost wp-miss-clear', 'Forget these');
  clear.title = 'Clears the record. The next plan will stop working around them.';
  clear.addEventListener('click', () => {
    S.weekly.misses = [];
    S.weekly.moved = {};
    // both of them are rulings the solver obeys, so forgetting them re-solves
    touchPlan();
    save();
    renderWeekly();
  });
  host.appendChild(clear);
}

export function renderWeekStatus() {
  const dot = $('wpDot');
  if (!dot) return;

  $('wpPlanLabel').textContent = 'Replan the week';

  const when = $('wpWhen');
  dot.className = 'sync-dot';
  if (WEEK.status === 'error') { dot.classList.add('is-bad'); when.textContent = (WEEK.message || 'the layout hit a problem').slice(0, 80); return; }
  /* The layout is always for this week, so this branch should never fire. It
     stays because it is the guard that makes that claim checkable, and a week
     grid drawn from the wrong week's blocks is worse than an empty one. */
  if (!weekPlanIsCurrent()) { dot.classList.add('is-stale'); when.textContent = 'planned for a different week'; return; }

  dot.classList.add('is-ok');
  when.textContent = WEEK.blocks.length
    ? `${WEEK.blocks.length} blocks, laid out just now`
    : 'nothing to lay out yet';
}

export function renderWeekly() {
  if (!$('wpGrid')) return;
  recomputePlans();
  renderWeekStatus();
  renderWeekGrid();
  renderWkItems();
  renderWeekNotes();
  renderMisses();

  renderSourceList('weekly', 'wpSources',
    'Nothing attached. Drop a screenshot of your timetable, or paste the section times out of an email.');
  const badge = $('wpSourceCount');
  if (badge) {
    badge.textContent = String(S.weekly.sources.length);
    badge.classList.toggle('is-zero', !S.weekly.sources.length);
  }
}

export function renderWkGroupFilter() {
  const sel = $('wpGroupFilter');
  if (!sel) return;
  const current = S.weekly.filterCourse;
  sel.textContent = '';
  sel.appendChild(new Option('All groups', ''));
  S.courses.forEach((c) => sel.appendChild(new Option(c.name, c.id)));
  sel.value = S.courses.some((c) => c.id === current) ? current : '';
  S.weekly.filterCourse = sel.value;
}

export function initWeeklyTab() {
  $('wpAdd').addEventListener('click', () => openWkItem(null));
  /* Kept, like its twin on the horizon: the count is instant, but being able
     to ask for it is part of trusting the answer. */
  $('wpPlan').addEventListener('click', () => {
    recomputePlans(true);
    renderWeekly();
    toast('Replanned.');
  });
  $('wpSave').addEventListener('click', saveWkItem);
  $('wpDelete').addEventListener('click', deleteWkItem);

  initSourceIntake('weekly', {
    zoneId: 'wpZone', inputId: 'wpFile',
    panelId: 'tab-weekly', pasteBtnId: 'wpPasteText',
  });

  [...$('wpKindSeg').children].forEach((b) =>
    b.addEventListener('click', () => setWkKind(b.dataset.kind)));

  // the day picker is seven toggles, because "Mon Wed Fri" is how a week is said
  DOW.forEach((name, jsDay) => {
    const b = el('button', 'wp-dow', name);
    b.type = 'button';
    b.dataset.dow = jsDay;
    b.addEventListener('click', () => b.classList.toggle('is-on'));
    $('wpDays').appendChild(b);
  });

  $('wpRecur').addEventListener('change', (e) => setWkRecur(e.target.value));
  ['wpEffortH', 'wpEffortM', 'wpTitle'].forEach((id) => $(id).addEventListener('input', updateWkMeta));

  $('wpAddCourse').addEventListener('click', () => {
    askText('Add group', 'Group name', '', (name) => {
      const c = { id: uid(), name, color: COURSE_COLORS[S.courses.length % COURSE_COLORS.length] };
      S.courses.push(c);
      save();
      renderCourseSelects();
      const sel = $('wpCourse');
      sel.textContent = '';
      S.courses.forEach((x) => sel.appendChild(new Option(x.name, x.id)));
      sel.value = c.id;
    });
  });

  $('wpGroupFilter').addEventListener('change', (e) => {
    S.weekly.filterCourse = e.target.value;
    save();
    renderWeekly();
  });

  const hours = () => {
    S.weekly.dayStart = hhmmToMin($('wpDayStart').value) ?? 480;
    S.weekly.dayEnd = Math.max(S.weekly.dayStart + 60, hhmmToMin($('wpDayEnd').value) ?? 1320);
    $('wpDayEnd').value = minToHhmm(S.weekly.dayEnd);
    // the envelope is the whole of the week's capacity, so this is a recount
    touchPlan();
    save();
    renderWeekly();
  };
  $('wpDayStart').addEventListener('change', hours);
  $('wpDayEnd').addEventListener('change', hours);
  $('wpDayStart').value = minToHhmm(S.weekly.dayStart);
  $('wpDayEnd').value = minToHhmm(S.weekly.dayEnd);

  renderWkGroupFilter();
}

/* ═════════════════════ 7. RECURRING SERIES ═════════════════════
   A reading and discussion post due every Tuesday at nine is one decision, not
   fourteen tasks. So it is stored once, as a template, and the tasks are
   generated from it a few weeks at a time.

   Three things follow from that, and they are the whole section:

     · A generated instance is an *ordinary task*. It goes into S.tasks with the
       same fields as anything typed into the New drawer, which is why the
       timer, the pomodoro, the finish dialog, the multiple, the day dock and
       the session view all work on it without knowing it came from a series.
       All it carries extra is a back-reference saying which template and which
       occurrence it is.

     · Departures from the pattern live on the template, keyed by the
       occurrence's own date. Reading week is a skip; a paper moved to the
       Thursday is a re-date. Neither disturbs the weeks around it, and because
       the key is the *original* date, re-dating an occurrence can never cause
       it to be generated a second time.

     · The point of asking what a task is made of before timing it is
       measurement. "Reading" is not a unit; forty pages of a primary source is.
       Once a few of those are finished, studyHub knows what a page costs this
       user in this course, and can predict the next one from its length rather
       than from an average of unlike things. That number is what the weekly
       planner is given, and it is the difference between "start it sometime
       Thursday" and "this one is ninety pages, it needs both evenings".      */

/* The attribute schemas, as data. Adding "problem set → number of questions"
   is an entry in this table and nothing else: the intake form, the storage,
   the rate buckets and the weekly payload are all driven from here.

   `type: 'quantity'` marks the field work is measured *per*. `type: 'choice'`
   splits the measurements, because a page of Hegel and a page of a textbook
   are not the same page. */
